import React, { useEffect, useRef, useState } from "react";
import {
    Loader2,
    Megaphone,
    Pencil,
    RefreshCw,
    Save,
    X,
} from "lucide-react";
import { toast } from "react-toastify";
import {
    getMinimumLettersMessage,
    hasMinimumLetters,
} from "../../utils/textValidation";

const ProductDashboardDetails = ({
    product,
    status,
    error,
    updateStatus,
    onRetry,
    onSave,
    onCreateCampaign,
    onClearUpdateState,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [draftName, setDraftName] = useState("");
    const [draftDescription, setDraftDescription] = useState("");
    const nameInputRef = useRef(null);
    const productId = product?.id || product?.product_id || product?.productId;
    const isSaving = updateStatus === "loading";
    const canSaveProduct =
        hasMinimumLetters(draftName) && hasMinimumLetters(draftDescription);

    useEffect(() => {
        setIsEditing(false);
        setDraftName(product?.name || "");
        setDraftDescription(product?.description || "");
        onClearUpdateState?.();
    }, [onClearUpdateState, productId, product?.description, product?.name]);

    useEffect(() => {
        if (!isEditing) return;

        nameInputRef.current?.focus();
    }, [isEditing]);

    const handleEdit = () => {
        setDraftName(product?.name || "");
        setDraftDescription(product?.description || "");
        onClearUpdateState?.();
        setIsEditing(true);
    };

    const handleCancel = () => {
        setDraftName(product?.name || "");
        setDraftDescription(product?.description || "");
        onClearUpdateState?.();
        setIsEditing(false);
    };

    const handleSave = async () => {
        const name = draftName.trim();
        const description = draftDescription.trim();

        if (!name || !description) {
            toast.error(
                "Product name and product description are required.",
            );
            return;
        }

        if (!hasMinimumLetters(name)) {
            toast.error(getMinimumLettersMessage("Product name"));
            return;
        }

        if (!hasMinimumLetters(description)) {
            toast.error(getMinimumLettersMessage("Product description"));
            return;
        }

        if (
            name === String(product?.name || "").trim() &&
            description === String(product?.description || "").trim()
        ) {
            setIsEditing(false);
            return;
        }

        const didSave = await onSave?.({ name, description });

        if (didSave) setIsEditing(false);
    };

    if (productId && status === "failed") {
        return (
            <main className="flex h-[36rem] min-h-0 items-center justify-center rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6 lg:h-full">
                <div className="w-full max-w-md rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    <p className="font-semibold">
                        Unable to load product details.
                    </p>
                    {error && <p className="mt-1">{error}</p>}
                    <button
                        type="button"
                        onClick={onRetry}
                        className="mt-3 inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 font-semibold hover:bg-red-50"
                    >
                        <RefreshCw size={15} />
                        Retry
                    </button>
                </div>
            </main>
        );
    }

    return (
        <main className="flex h-[36rem] min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm lg:h-full">
            <div className="shrink-0 border-b border-indigo-100 bg-[#eef2ff] p-4">
                <h2 className="truncate text-lg font-semibold text-[#0f172a]">
                    Product Details
                </h2>
                <p className="mt-1 text-sm text-[#475569]">
                    Review and update product information.
                </p>
            </div>

            <div className="product-scrollbar min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
                {!productId && status === "loading" ? (
                    <div className="flex h-full min-h-[20rem] items-center justify-center">
                        <div className="flex items-center gap-2 text-sm font-semibold text-indigo-700">
                            <Loader2 size={18} className="animate-spin" />
                            Loading product details...
                        </div>
                    </div>
                ) : !productId ? (
                    <div className="flex h-full min-h-[20rem] items-center justify-center">
                        <div className="max-w-sm text-center">
                            <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
                                <Pencil size={20} aria-hidden="true" />
                            </div>
                            <h2 className="mt-3 text-lg font-semibold text-slate-900">
                                Select a product
                            </h2>
                            <p className="mt-1 text-sm text-slate-500">
                                Choose a product from the list to view its
                                details.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div>
                            <label
                                htmlFor="dashboard-product-name"
                                className="mb-1.5 block text-sm font-semibold text-slate-700"
                            >
                                Name
                            </label>
                            <input
                                ref={nameInputRef}
                                id="dashboard-product-name"
                                type="text"
                                readOnly={!isEditing}
                                value={
                                    isEditing ? draftName : product?.name || ""
                                }
                                onChange={(event) => {
                                    setDraftName(event.target.value);
                                    onClearUpdateState?.();
                                }}
                                className={`w-full rounded-xl border px-4 py-2.5 text-sm text-slate-900 outline-none transition ${
                                    isEditing
                                        ? "border-slate-200 bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                                        : "border-slate-200 bg-slate-50"
                                }`}
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="dashboard-product-description"
                                className="mb-1.5 block text-sm font-semibold text-slate-700"
                            >
                                Description
                            </label>
                            <textarea
                                id="dashboard-product-description"
                                rows={8}
                                readOnly={!isEditing}
                                value={
                                    isEditing
                                        ? draftDescription
                                        : product?.description || ""
                                }
                                onChange={(event) => {
                                    setDraftDescription(event.target.value);
                                    onClearUpdateState?.();
                                }}
                                className={`w-full resize-y rounded-xl border px-4 py-3 text-sm leading-6 text-slate-900 outline-none transition ${
                                    isEditing
                                        ? "border-slate-200 bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                                        : "border-slate-200 bg-slate-50"
                                }`}
                            />
                        </div>
                    </div>
                )}
            </div>

            {productId && (
                <footer className="shrink-0 bg-white p-4 sm:px-5">
                    <div className="flex items-center justify-end gap-3">
                        {isEditing ? (
                            <>
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
                                >
                                    <X size={16} />
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={isSaving || !canSaveProduct}
                                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {isSaving ? (
                                        <Loader2
                                            size={16}
                                            className="animate-spin"
                                        />
                                    ) : (
                                        <Save size={16} />
                                    )}
                                    {isSaving ? "Saving..." : "Save Changes"}
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={handleEdit}
                                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-indigo-50 px-3 py-2.5 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100 sm:px-4"
                                >
                                    <Pencil size={16} />
                                    Edit Product
                                </button>
                                <button
                                    type="button"
                                    onClick={onCreateCampaign}
                                    disabled={!onCreateCampaign}
                                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 sm:px-4"
                                >
                                    <Megaphone size={16} />
                                    Initiate Campaign
                                </button>
                            </>
                        )}
                    </div>
                </footer>
            )}
        </main>
    );
};

export default ProductDashboardDetails;
